import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AssetsState } from './assets.state';
import { Result } from '../../shared/models/core/result.model';
import { Pavement } from '../../shared/models/operation/pavement.model';
import { Bollard } from '../../shared/models/operation/bollard.model';
import { Bridge } from '../../shared/models/operation/bridge.model';
import { BusStopModel } from '../../shared/models/operation/bus-stop.model';
import { DrainageCover } from '../../shared/models/operation/drainage-cover.model';
import { Drainage } from '../../shared/models/operation/drainage.model';
import { Fence } from '../../shared/models/operation/fence.model';
import { GeoTechStructure } from '../../shared/models/operation/geo-tech-structure.model';
import { Guardrail } from '../../shared/models/operation/guardrail.model';
import { MultipurposeHall } from '../../shared/models/operation/multipurpose-hall.model';
import { Parking } from '../../shared/models/operation/parking.model';
import { PublicToilet } from '../../shared/models/operation/public-toilet.model';
import { RoadKerb } from '../../shared/models/operation/road-kerb.model';
import { RoadMarking } from '../../shared/models/operation/road-marking.model';
import { TrafficLight } from '../../shared/models/operation/traffic-light.model';
import { TrafficSign } from '../../shared/models/operation/traffic-sign.model';
import { Walkway } from '../../shared/models/operation/walkway.model';
import { BinCentre } from '../../shared/models/operation/bin-centre.model';
import { MaintenanceAsset } from '../../shared/models/operation/maintenance-asset.model';

export const selectAssetsState = createFeatureSelector<AssetsState>('assets');

export const selectAssetsLoading = createSelector(selectAssetsState, (state) => state.loading);
export const selectAssetsError = createSelector(selectAssetsState, (state) => state.error);

// Helper for result selectors
const createResultSelectors = <T>(key: keyof AssetsState) => {
    const selectResult = createSelector(selectAssetsState, (state) => state[key] as Result<T[]>);
    return {
        selectResult,
        selectData: createSelector(selectResult, (result) => result.data),
        selectTotal: createSelector(selectResult, (result) => result.total),
        selectPage: createSelector(selectResult, (result) => result.page),
        selectSize: createSelector(selectResult, (result) => result.size),
    };
};

export const {
    selectResult: selectPavementsResult,
    selectData: selectPavementsData,
    selectTotal: selectPavementsTotal,
    selectPage: selectPavementsPage,
    selectSize: selectPavementsSize
} = createResultSelectors<Pavement>('pavementResult');

export const {
    selectResult: selectBollardsResult,
    selectData: selectBollardsData,
    selectTotal: selectBollardsTotal,
    selectPage: selectBollardsPage,
    selectSize: selectBollardsSize
} = createResultSelectors<Bollard>('bollardResult');

export const {
    selectResult: selectBridgesResult,
    selectData: selectBridgesData,
    selectTotal: selectBridgesTotal,
    selectPage: selectBridgesPage,
    selectSize: selectBridgesSize
} = createResultSelectors<Bridge>('bridgeResult');

export const {
    selectResult: selectBusStopsResult,
    selectData: selectBusStopsData,
    selectTotal: selectBusStopsTotal,
    selectPage: selectBusStopsPage,
    selectSize: selectBusStopsSize
} = createResultSelectors<BusStopModel>('busStopResult');

export const {
    selectResult: selectDrainageCoversResult,
    selectData: selectDrainageCoversData,
    selectTotal: selectDrainageCoversTotal,
    selectPage: selectDrainageCoversPage,
    selectSize: selectDrainageCoversSize
} = createResultSelectors<DrainageCover>('drainageCoverResult');

export const {
    selectResult: selectDrainagesResult,
    selectData: selectDrainagesData,
    selectTotal: selectDrainagesTotal,
    selectPage: selectDrainagesPage,
    selectSize: selectDrainagesSize
} = createResultSelectors<Drainage>('drainageResult');

export const {
    selectResult: selectFencesResult,
    selectData: selectFencesData,
    selectTotal: selectFencesTotal,
    selectPage: selectFencesPage,
    selectSize: selectFencesSize
} = createResultSelectors<Fence>('fenceResult');

export const {
    selectResult: selectGeoTechStructuresResult,
    selectData: selectGeoTechStructuresData,
    selectTotal: selectGeoTechStructuresTotal,
    selectPage: selectGeoTechStructuresPage,
    selectSize: selectGeoTechStructuresSize
} = createResultSelectors<GeoTechStructure>('geoTechStructureResult');

export const {
    selectResult: selectGuardrailsResult,
    selectData: selectGuardrailsData,
    selectTotal: selectGuardrailsTotal,
    selectPage: selectGuardrailsPage,
    selectSize: selectGuardrailsSize
} = createResultSelectors<Guardrail>('guardrailResult');

export const {
    selectResult: selectMultipurposeHallsResult,
    selectData: selectMultipurposeHallsData,
    selectTotal: selectMultipurposeHallsTotal,
    selectPage: selectMultipurposeHallsPage,
    selectSize: selectMultipurposeHallsSize
} = createResultSelectors<MultipurposeHall>('multipurposeHallResult');

export const {
    selectResult: selectParkingResult,
    selectData: selectParkingData,
    selectTotal: selectParkingTotal,
    selectPage: selectParkingPage,
    selectSize: selectParkingSize
} = createResultSelectors<Parking>('parkingResult');

export const {
    selectResult: selectPublicToiletsResult,
    selectData: selectPublicToiletsData,
    selectTotal: selectPublicToiletsTotal,
    selectPage: selectPublicToiletsPage,
    selectSize: selectPublicToiletsSize
} = createResultSelectors<PublicToilet>('publicToiletResult');

export const {
    selectResult: selectRoadKerbsResult,
    selectData: selectRoadKerbsData,
    selectTotal: selectRoadKerbsTotal,
    selectPage: selectRoadKerbsPage,
    selectSize: selectRoadKerbsSize
} = createResultSelectors<RoadKerb>('roadKerbResult');

export const {
    selectResult: selectRoadMarkingsResult,
    selectData: selectRoadMarkingsData,
    selectTotal: selectRoadMarkingsTotal,
    selectPage: selectRoadMarkingsPage,
    selectSize: selectRoadMarkingsSize
} = createResultSelectors<RoadMarking>('roadMarkingResult');

export const {
    selectResult: selectTrafficLightsResult,
    selectData: selectTrafficLightsData,
    selectTotal: selectTrafficLightsTotal,
    selectPage: selectTrafficLightsPage,
    selectSize: selectTrafficLightsSize
} = createResultSelectors<TrafficLight>('trafficLightResult');

export const {
    selectResult: selectTrafficSignsResult,
    selectData: selectTrafficSignsData,
    selectTotal: selectTrafficSignsTotal,
    selectPage: selectTrafficSignsPage,
    selectSize: selectTrafficSignsSize
} = createResultSelectors<TrafficSign>('trafficSignResult');

export const {
    selectResult: selectWalkwaysResult,
    selectData: selectWalkwaysData,
    selectTotal: selectWalkwaysTotal,
    selectPage: selectWalkwaysPage,
    selectSize: selectWalkwaysSize
} = createResultSelectors<Walkway>('walkwayResult');

export const {
    selectResult: selectBinCentresResult,
    selectData: selectBinCentresData,
    selectTotal: selectBinCentresTotal,
    selectPage: selectBinCentresPage,
    selectSize: selectBinCentresSize
} = createResultSelectors<BinCentre>('binCentreResult');

export const {
    selectResult: selectMaintenanceAssetsResult,
    selectData: selectMaintenanceAssetsData,
    selectTotal: selectMaintenanceAssetsTotal,
    selectPage: selectMaintenanceAssetsPage,
    selectSize: selectMaintenanceAssetsSize
} = createResultSelectors<MaintenanceAsset>('maintenanceAssetResult');
